// ═══════════════════════════════════════════════════════════════════
//  modules/courriel/Courriel.js
//  Courriel
// ═══════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useRef, useState } from "react";
import { API_BASE } from "../../socle/api";
import { useIsMobile } from "../../socle/hooks";
import { THEME } from "../../socle/theme";
import { LIBELLES_DOSSIERS } from "./constantes";


import { MaSignature } from "./MaSignature";
import { Redaction } from "./Redaction";

import { mailDate, mailHeaders } from "./utils";
import { CourrielDossiers } from "./CourrielDossiers";
import { CourrielEntete } from "./CourrielEntete";
import { CourrielFiltres } from "./CourrielFiltres";
import { CourrielLecture } from "./CourrielLecture";
import { CourrielListe } from "./CourrielListe";
import { CourrielTuiles } from "./CourrielTuiles";

// ═══════════════════════════════════════════════════════════════════════
//  MODULE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════
export function Courriel({ token, user }) {
  const isMobile = useIsMobile();
  const [liste, setListe] = useState(null);
  const [stats, setStats] = useState(null);
  const [dossiers, setDossiers] = useState([]);
  const [dossier, setDossier] = useState("inbox");    // inbox | sent | spam | trash
  const [selection, setSelection] = useState(null);   // message ouvert
  const [chargementMsg, setChargementMsg] = useState(false);
  const [filtre, setFiltre] = useState("");           // étiquette (Reçus uniquement)
  const [recherche, setRecherche] = useState("");
  const [erreur, setErreur] = useState(null);
  const [redaction, setRedaction] = useState(null);   // { to, subject, body, replyToUid }
  const [signatureOuverte, setSignatureOuverte] = useState(false);
  const [reindexation, setReindexation] = useState(false);
  const [imagesVisibles, setImagesVisibles] = useState(false);

  const estBoiteRecue = dossier === "inbox";

  // Dossier réellement affiché à l'instant T, lisible depuis n'importe quelle
  // requête en vol — un state seul serait figé dans les fonctions capturées.
  const dossierRef = useRef(dossier);
  useEffect(() => { dossierRef.current = dossier; }, [dossier]);

  const chargerDossiers = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/mail/folders`, { headers: mailHeaders(token) });
      if (!res.ok) return;
      const json = await res.json();
      if (json.folders) setDossiers(json.folders);
    } catch { /* les onglets restent sur le repli statique */ }
  }, [token]);

  const charger = useCallback(async () => {
    // Le dossier demandé est figé ici : si l'utilisateur change d'onglet
    // pendant la requête, la réponse tardive est ignorée au lieu d'écraser
    // la liste affichée.
    const demande = dossier;
    try {
      const params = new URLSearchParams({ limit: "50", folder: demande });
      if (filtre && estBoiteRecue) params.set("tag", filtre);
      if (recherche.trim()) params.set("q", recherche.trim());
      const res = await fetch(`${API_BASE}/api/mail/list?${params}`, { headers: mailHeaders(token) });
      if (demande !== dossierRef.current) return;
      if (!res.ok) throw new Error("Le serveur n'a pas répondu correctement.");
      const json = await res.json();
      if (demande !== dossierRef.current) return;
      if (json.configured === false) { setErreur("Le module n'est pas configuré : variables MAIL_* absentes du .env."); return; }
      if (json.absent) {
        setListe({ messages: [], total: 0, unread: 0 });
        setErreur(`Ce serveur n'expose pas de dossier « ${LIBELLES_DOSSIERS[dossier] || dossier} ».`);
        return;
      }
      setListe(json);
      setErreur(null);
    } catch (e) {
      setErreur("Boîte mail injoignable. Vérifie la connexion IMAP avec /api/mail/diag.");
    }
  }, [dossier, estBoiteRecue, filtre, recherche, token]);

  const chargerStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/mail/stats`, { headers: mailHeaders(token) });
      if (res.ok) setStats(await res.json());
    } catch { /* les tuiles restent vides, sans conséquence */ }
  }, [token]);

  useEffect(() => { setSelection(null); charger(); }, [filtre, dossier, charger]);
  useEffect(() => { chargerStats(); chargerDossiers(); }, [chargerDossiers, chargerStats]);

  // Le rafraîchissement est recréé à chaque changement de dossier, de filtre
  // ou de recherche : sinon il conserverait indéfiniment les valeurs qu'il
  // avait au montage et rechargerait toujours les Reçus.
  useEffect(() => {
    const id = setInterval(() => { charger(); chargerStats(); chargerDossiers(); }, 60000);
    return () => clearInterval(id);
  }, [dossier, filtre, recherche, charger, chargerDossiers, chargerStats]);

  const ouvrir = async (uid) => {
    setChargementMsg(true); setImagesVisibles(false);
    try {
      const res = await fetch(`${API_BASE}/api/mail/message/${uid}?folder=${dossier}`, { headers: mailHeaders(token) });
      if (!res.ok) throw new Error((await res.json()).error || "illisible");
      setSelection(await res.json());
      charger();
    } catch (e) {
      setSelection(null);
      setErreur("Impossible d'ouvrir ce message : " + e.message);
    } finally { setChargementMsg(false); }
  };

  const changerTag = async (uid, tag) => {
    // « Indésirable » a un seul sens : le message part dans le dossier
    // Indésirables du serveur. Il sort de l'index, donc de tous les compteurs.
    if (tag === "spam") return deplacer(uid, "spam");

    await fetch(`${API_BASE}/api/mail/message/${uid}/tag`, {
      method: "PATCH", headers: mailHeaders(token), body: JSON.stringify({ tag }),
    });
    charger(); chargerStats();
    if (selection && selection.uid === uid) setSelection({ ...selection, tag });
  };

  const creerTicket = async (uid) => {
    const res = await fetch(`${API_BASE}/api/mail/message/${uid}/ticket`, {
      method: "POST", headers: mailHeaders(token), body: JSON.stringify({}),
    });
    const json = await res.json();
    if (json.success) {
      setSelection((s) => (s && s.uid === uid ? { ...s, ticketId: json.id } : s));
      charger(); chargerStats();
    } else {
      setErreur(json.error || "Création du ticket impossible.");
    }
  };

  const relancerIA = async (uid) => {
    setSelection((s) => (s ? { ...s, iaStatus: "en_attente" } : s));
    const res = await fetch(`${API_BASE}/api/mail/message/${uid}/analyse`, { method: "POST", headers: mailHeaders(token) });
    const json = await res.json();
    if (json.success && json.ia) {
      setSelection((s) => (s && s.uid === uid ? { ...s, iaStatus: json.ia.ia_status, ia: {
        intention: json.ia.ia_intention, sentiment: json.ia.ia_sentiment,
        urgence: json.ia.ia_urgence, action: json.ia.ia_action, resume: json.ia.ia_resume,
      } } : s));
      charger();
    }
  };

  const supprimer = async (uid) => {
    await fetch(`${API_BASE}/api/mail/message/${uid}?folder=${dossier}`, { method: "DELETE", headers: mailHeaders(token) });
    setSelection(null); charger(); chargerStats(); chargerDossiers();
  };

  // Réindexation des Reçus : utile si la liste paraît vide alors que la
  // boîte contient des messages (watcher démarré trop tôt, par exemple).
  const reindexer = async () => {
    setReindexation(true); setErreur(null);
    try {
      const res = await fetch(`${API_BASE}/api/mail/resync`, {
        method: "POST", headers: mailHeaders(token), body: JSON.stringify({ nombre: 200 }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "échec");
      await charger(); await chargerStats();
      if (json.ajoutes === 0) setErreur("Aucun message à indexer : la boîte de réception est vide côté serveur.");
    } catch (e) {
      setErreur("Réindexation impossible : " + e.message);
    } finally { setReindexation(false); }
  };

  // Déplacement entre dossiers : « marquer indésirable », « ce n'est pas
  // un indésirable », « restaurer depuis la corbeille »…
  const deplacer = async (uid, cible) => {
    const res = await fetch(`${API_BASE}/api/mail/message/${uid}/move`, {
      method: "POST", headers: mailHeaders(token),
      body: JSON.stringify({ from: dossier, to: cible }),
    });
    const json = await res.json();
    if (json.success) { setSelection(null); charger(); chargerStats(); chargerDossiers(); }
    else setErreur(json.error || "Déplacement impossible.");
  };

  const repondre = () => {
    if (!selection) return;
    // Dans « Envoyés », on relance le destinataire, pas soi-même.
    const exp = dossier === "sent"
      ? (selection.to && selection.to[0]) || {}
      : selection.from || {};
    const citation = (selection.text || "")
      .split("\n").slice(0, 20).map((l) => "> " + l).join("\n");
    setRedaction({
      to: exp.address || "",
      subject: selection.subject.startsWith("Re:") ? selection.subject : `Re: ${selection.subject}`,
      body: `\n\n${mailDate(selection.date)}, ${exp.name || exp.address} a écrit :\n${citation}`,
      replyToUid: selection.uid,
      prenom: (exp.name || exp.address || "").split(/[\s@.]/)[0],
      sujetOrigine: selection.subject,
    });
  };

  // ── Rendu ─────────────────────────────────────────────────────────────
  const messages = liste?.messages || [];

  return (
    <div>
      {/* En-tête */}
      <CourrielEntete charger={charger} chargerDossiers={chargerDossiers} chargerStats={chargerStats} dossier={dossier} liste={liste} setRedaction={setRedaction} setSignatureOuverte={setSignatureOuverte} />

      {/* Onglets de dossiers */}
      <CourrielDossiers dossier={dossier} dossiers={dossiers} setDossier={setDossier} setFiltre={setFiltre} setRecherche={setRecherche} />

      {erreur && (
        <div style={{ background: THEME.redDim, color: THEME.red, border: `1px solid ${THEME.red}`, borderRadius: 8, padding: 11, marginBottom: 14, fontSize: 13 }}>
          {erreur}
        </div>
      )}

      {/* Tuiles — indicateurs de la boîte de réception uniquement */}
      <CourrielTuiles estBoiteRecue={estBoiteRecue} isMobile={isMobile} liste={liste} stats={stats} />

      {/* Filtres — les étiquettes et l'analyse IA ne concernent que les Reçus */}
      <CourrielFiltres charger={charger} dossier={dossier} estBoiteRecue={estBoiteRecue} filtre={filtre} recherche={recherche} setFiltre={setFiltre} setRecherche={setRecherche} stats={stats} />

      {/* Liste + lecture */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(300px, 420px) 1fr", gap: 14, alignItems: "start" }}>

        {/* Colonne liste */}
        <CourrielListe dossier={dossier} estBoiteRecue={estBoiteRecue} filtre={filtre} isMobile={isMobile} liste={liste} messages={messages} ouvrir={ouvrir} recherche={recherche} reindexation={reindexation} reindexer={reindexer} selection={selection} />

        {/* Colonne lecture */}
        <CourrielLecture changerTag={changerTag} chargementMsg={chargementMsg} creerTicket={creerTicket} deplacer={deplacer} dossier={dossier} estBoiteRecue={estBoiteRecue} imagesVisibles={imagesVisibles} isMobile={isMobile} relancerIA={relancerIA} repondre={repondre} selection={selection} setImagesVisibles={setImagesVisibles} setSelection={setSelection} supprimer={supprimer} token={token} />
      </div>

      {redaction && (
        <Redaction token={token} initial={redaction}
                   onClose={() => setRedaction(null)}
                   onSent={(avertissement) => {
                     setRedaction(null);
                     if (avertissement) setErreur(avertissement);
                     charger(); chargerStats(); chargerDossiers();
                   }} />
      )}

      {signatureOuverte && (
        <MaSignature token={token} user={user} onClose={() => setSignatureOuverte(false)} />
      )}
    </div>
  );
}
